import { reportErrorToSentry } from './setup/sentry';
import { processAccountsApiResponse } from '../features/api/mapper';
import { accountType } from './login/loginSlice';
import { accountsApi } from '../features/api/accountsApi';
import { store } from './setup/store';

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retries an async function with exponential backoff.
 * @param operation The async function to retry.
 * @param maxAttempts Maximum number of attempts (including the initial one).
 * @param maxDelayMs Maximum delay between retries in milliseconds.
 * @param baseDelayMs Initial delay in milliseconds.
 * @returns The result of the operation if successful.
 * @throws The last error encountered if all attempts fail.
 */
const retryAsyncOperation = async <T>(
    operation: () => Promise<T>,
    maxAttempts: number,
    maxDelayMs: number,
    baseDelayMs = 200 // Start with a 200ms delay
): Promise<T> => {
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            // Attempt the operation
            return await operation();
        } catch (error) {
            lastError = error; // Store the error

            // If this was the last attempt, break the loop (error will be thrown after the loop)
            if (attempt === maxAttempts) {
                break;
            }

            // Calculate exponential backoff delay, capped by maxDelayMs
            // Delay = min(maxDelayMs, baseDelayMs * 2^(attempt - 1))
            const delayTime = Math.min(maxDelayMs, baseDelayMs * 2 ** (attempt - 1));

            console.warn(
                `Attempt ${attempt} failed for operation. Retrying in ${delayTime}ms... Error:`,
                error instanceof Error ? error.message : error // Log error message if available
            );

            // Wait before the next attempt
            await delay(delayTime);
        }
    }
    // If loop finished without returning, all attempts failed. Throw the last recorded error.
    console.error(`Operation failed after ${maxAttempts} attempts.`);
    throw lastError;
};

export const getAccountType = async (accountId: string, token: string) => {
    const operation = async () => {
        const request = store.dispatch(
            accountsApi.endpoints.getRawAccount.initiate(
                {
                    accountId,
                    tokenOverride: token,
                },
                {
                    forceRefetch: true,
                }
            )
        );

        try {
            const account = await request.unwrap();
            return processAccountsApiResponse(account);
        } finally {
            request.unsubscribe();
        }
    };

    try {
        const account = await retryAsyncOperation(operation, 3, 2000);
        return account.accountType;
    } catch (e: unknown) {
        const userAgent = typeof window !== 'undefined' ? window?.navigator?.userAgent : '';
        if (/headless/i.test(userAgent ?? '')) {
            // do nothing for headless. This is capability monitoring
        } else {
            reportErrorToSentry(e);
        }
        // use CARRIER as fallback
        return accountType.UNKNOWN;
    }
};
